import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import * as repo from '@/db/repositories'
import type { FavoriteRow } from '@/db/repositories'

/**
 * Store de favoritos cross-device.
 *
 * Cada favorito es solo un `{ id: bookId, addedAt }`. La fuente de
 * verdad es el doc CRDT `favorites:${userId}` (ver `useFavoritesSync`);
 * esta tabla IndexedDB es caché local para rellenar la UI sin esperar
 * al sync al arrancar, igual que las estanterías.
 *
 * Las mutaciones SIEMPRE reasignan `favorites.value` de forma inmutable
 * (nunca mutan in-place): el puente de sync `connectCollection` no usa
 * `deep:true`, así que solo detecta reasignaciones del ref. Coherente
 * con `useShelvesStore`.
 *
 * Un libro es favorito si está en este set, **sin importar si está
 * descargado o es cloud-only**. El `book.favorite` local (IndexedDB) se
 * mantiene por compatibilidad con la UI existente, pero el set CRDT es
 * el estado canónico: al togglear desde `BookCard` (local) o desde
 * `CloudBookCard` (cloud), el cambio se refleja aquí.
 */
export const useFavoritesStore = defineStore('favorites', () => {
  const favorites = ref<FavoriteRow[]>([])
  const loading = ref(false)

  async function loadAll() {
    loading.value = true
    try {
      favorites.value = await repo.getAllFavorites()
    } finally {
      loading.value = false
    }
  }

  /** Reemplaza la lista entera (lo usa el puente CRDT al llegar cambios remotos). */
  function replaceAll(items: FavoriteRow[]) {
    favorites.value = items.slice()
  }

  /** Set de bookIds favoritos, para búsqueda O(1) desde la UI. */
  const favoriteIds = computed(() => new Set(favorites.value.map((f) => f.id)))

  function isFavorite(bookId: string): boolean {
    return favoriteIds.value.has(bookId)
  }

  /** Marca un libro como favorito (idempotente). */
  async function addFavorite(bookId: string) {
    if (favoriteIds.value.has(bookId)) return
    const row: FavoriteRow = { id: bookId, addedAt: new Date().toISOString() }
    await repo.putFavorite(row)
    favorites.value = [...favorites.value, row]
  }

  /** Quita un libro de favoritos (idempotente). */
  async function removeFavorite(bookId: string) {
    if (!favoriteIds.value.has(bookId)) return
    await repo.deleteFavorite(bookId)
    favorites.value = favorites.value.filter((f) => f.id !== bookId)
  }

  /** Alterna el estado de favorito. Devuelve el nuevo estado. */
  async function toggleFavorite(bookId: string): Promise<boolean> {
    if (isFavorite(bookId)) {
      await removeFavorite(bookId)
      return false
    }
    await addFavorite(bookId)
    return true
  }

  /**
   * Migración suave (una sola vez por dispositivo): siembra el set CRDT
   * de favoritos con los libros locales marcados como `favorite: true`
   * que aún no estén en el set. Así no se pierden los favoritos que el
   * usuario tenía antes de que existiera el CRDT cross-device.
   *
   * Idempotente: solo añade lo que falta. Se llama tras `loadAll` +
   * `openFavoritesSync` en App.vue cuando hay sesión.
   */
  async function seedFromLocalBooks(localFavoriteIds: string[]): Promise<void> {
    const missing = localFavoriteIds.filter((id) => !favoriteIds.value.has(id))
    if (missing.length === 0) return
    const now = new Date().toISOString()
    const rows = missing.map((id) => ({ id, addedAt: now }))
    for (const row of rows) await repo.putFavorite(row)
    favorites.value = [...favorites.value, ...rows]
  }

  return {
    favorites,
    loading,
    favoriteIds,
    loadAll,
    replaceAll,
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    seedFromLocalBooks,
  }
})
